import { useMemo } from 'react';

import { CashFlowType, LifecycleStatus } from '@ndvr/portfolio-api';

import { Spinner } from '@/components/Spinner';
import { useRequiredParams } from 'utils';
import { useAccountsYtdTaxMetrics } from 'views/account/tax-metrics-queries';
import { useListAccountGroupProfilesWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useListCashflowConfigsForAccountGroups } from 'views/portfolio/account-group-queries';
import { useListPortfolioEngineAccountsForHousehold } from 'views/portfolio/account-queries';
import { getNextWithdrawal } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/cash-flow-schedule';
import { useAggregatedHoldingsReport, useAggregatedPerformanceReport } from 'views/portfolio/reporting-queries';

import { buildAccountsSummary } from './HouseholdAccountsTab/accounts-summary';
import { HouseholdAccountsSummaryCard } from './HouseholdAccountsTab/HouseholdAccountsSummaryCard';
import { HouseholdAccountTable } from './HouseholdAccountsTab/HouseholdAccountTable';
import { presetRange } from './reporting/date-ranges';

export const HouseholdAccountsTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: accountGroupProfiles = [], isLoading: isAccountGroupsLoading } =
        useListAccountGroupProfilesWithAumIncluded(globalAccountGUID);
    const { data: holdingsReport, isPending: isHoldingsLoading } = useAggregatedHoldingsReport({
        householdGUID: globalAccountGUID,
    });
    const { data: ytdPerformance, isPending: isYtdPerformanceLoading } = useAggregatedPerformanceReport({
        householdGUID: globalAccountGUID,
        ...presetRange('YTD'),
    });
    const { data: allTimePerformance, isPending: isAllTimePerformanceLoading } = useAggregatedPerformanceReport({
        householdGUID: globalAccountGUID,
        ...presetRange('ALL_TIME'),
    });
    const { data: householdPortfolioEngineAccounts = [], isLoading: isAccountsTableLoading } =
        useListPortfolioEngineAccountsForHousehold(globalAccountGUID);
    const profilesByGUID = useMemo(
        () => new Map(accountGroupProfiles.map((profile) => [profile.accountGroup.accountGroupGUID, profile])),
        [accountGroupProfiles],
    );
    const summaryAccountGroupGUIDs = useMemo(
        () =>
            accountGroupProfiles
                .filter(({ accountGroup }) => accountGroup.lifecycleStatus !== LifecycleStatus.ONBOARDING)
                .map(({ accountGroup }) => accountGroup.accountGroupGUID),
        [accountGroupProfiles],
    );
    const accountsSummary = useMemo(
        () => buildAccountsSummary(householdPortfolioEngineAccounts, new Set(summaryAccountGroupGUIDs)),
        [householdPortfolioEngineAccounts, summaryAccountGroupGUIDs],
    );
    const { boostedAccounts } = accountsSummary;
    const { data: cashFlowConfigs, isPending: isCashFlowConfigsLoading } =
        useListCashflowConfigsForAccountGroups(summaryAccountGroupGUIDs);
    const accountGUIDs = useMemo(
        () =>
            householdPortfolioEngineAccounts
                .filter(({ lifecycleStatus }) => lifecycleStatus === LifecycleStatus.REAL)
                .map(({ accountGUID }) => accountGUID),
        [householdPortfolioEngineAccounts],
    );
    const { data: ytdTaxMetrics = [], isPending: isYtdTaxMetricsLoading } = useAccountsYtdTaxMetrics(accountGUIDs);
    const taxLiabilityByAccountGUID = useMemo(
        () => new Map(ytdTaxMetrics.map(({ accountGuid, ytdTax }) => [accountGuid, ytdTax])),
        [ytdTaxMetrics],
    );

    if (isAccountGroupsLoading || isAccountsTableLoading) {
        return (
            <div className="flex h-48 items-center justify-center">
                <Spinner showText text="Loading accounts..." />
            </div>
        );
    }

    const boostedAccountGUIDs = new Set(boostedAccounts.map(({ accountGUID }) => accountGUID));
    const boostedAccountGroupGUIDs = new Set(boostedAccounts.map(({ accountGroupGUID }) => accountGroupGUID));
    const nextWithdrawal = getNextWithdrawal(
        cashFlowConfigs.filter(
            (config) =>
                config.cashFlowType === CashFlowType.WITHDRAWAL &&
                (config.accountGUID
                    ? boostedAccountGUIDs.has(config.accountGUID)
                    : boostedAccountGroupGUIDs.has(config.accountGroupGUID)),
        ),
    );
    const boostedYtdTaxMetrics = ytdTaxMetrics.filter(({ accountGuid }) => boostedAccountGUIDs.has(accountGuid));
    const ytdShortTermGainsValue = boostedYtdTaxMetrics.some(({ ytdStPnl }) => ytdStPnl !== undefined)
        ? boostedYtdTaxMetrics.reduce((total, { ytdStPnl = 0 }) => total + ytdStPnl, 0)
        : undefined;
    const ytdLongTermGainsValue = boostedYtdTaxMetrics.some(({ ytdLtPnl }) => ytdLtPnl !== undefined)
        ? boostedYtdTaxMetrics.reduce((total, { ytdLtPnl = 0 }) => total + ytdLtPnl, 0)
        : undefined;
    const {
        managedValue,
        heldAwayValue,
        boostedValue,
        boostedAccountCount,
        accountCount,
        totalCashValue,
        harvestableLossValue,
    } = accountsSummary;

    return (
        <div className="flex flex-col gap-6">
            <HouseholdAccountsSummaryCard
                managedValue={managedValue}
                heldAwayValue={heldAwayValue}
                boostedValue={boostedValue}
                boostedAccountCount={boostedAccountCount}
                accountCount={accountCount}
                totalCashValue={totalCashValue}
                harvestableLossValue={harvestableLossValue}
                ytdShortTermGainsValue={ytdShortTermGainsValue}
                ytdLongTermGainsValue={ytdLongTermGainsValue}
                isYtdTaxMetricsLoading={isYtdTaxMetricsLoading}
                nextWithdrawal={nextWithdrawal}
                isNextWithdrawalLoading={isCashFlowConfigsLoading}
                holdingsReport={holdingsReport}
                isHoldingsLoading={isHoldingsLoading}
                ytdPerformance={ytdPerformance}
                isYtdPerformanceLoading={isYtdPerformanceLoading}
                allTimePerformance={allTimePerformance}
                isAllTimePerformanceLoading={isAllTimePerformanceLoading}
            />
            <HouseholdAccountTable
                householdGUID={globalAccountGUID}
                accounts={householdPortfolioEngineAccounts}
                accountGroupProfiles={profilesByGUID}
                taxLiabilityByAccountGUID={taxLiabilityByAccountGUID}
                isTaxLiabilityLoading={isYtdTaxMetricsLoading}
                isLoading={isAccountsTableLoading}
            />
        </div>
    );
};
